/**
 * R16.1-A — Public Keyholder Leaderboard.
 *
 * No auth required. Real-data aggregate from `/api/leaderboard`.
 * Empty state explicit (no placeholders, no padding).
 *
 * Route: /leaderboard (public)
 *
 * Testids:
 *   - leaderboard-section
 *   - leaderboard-range-{slug}
 *   - leaderboard-sort-{slug}
 *   - leaderboard-row-{slug}
 *   - leaderboard-empty
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Users, MessageSquare, Bot, Clock, Activity, ExternalLink,
  ArrowLeft, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

const SORTS = [
  { value: "peak_concurrent", label: "Peak concurrent", short: "Peak", icon: Activity },
  { value: "participants", label: "Total participants", short: "Joined", icon: Users },
  { value: "chat_messages", label: "Chat messages", short: "Chat", icon: MessageSquare },
  { value: "sessions_hosted", label: "Sessions hosted", short: "Sessions", icon: Clock },
];

export default function Leaderboard() {
  const [range, setRange] = useState("30d");
  const [sort, setSort] = useState("peak_concurrent");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/leaderboard?range=${range}&sort=${sort}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) { setRows(d.rows || []); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Couldn't load leaderboard."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, sort]);

  const sortMeta = useMemo(() => SORTS.find((s) => s.value === sort) || SORTS[0], [sort]);

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="leaderboard-section">
      <header className="border-b border-white/10 bg-[#080810]/85 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="p-1.5 rounded hover:bg-white/5" data-testid="leaderboard-back">
            <ArrowLeft className="w-4 h-4 text-white/55" />
          </Link>
          <Trophy className="w-5 h-5 text-amber-300" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/85 font-bold">Full Spectrum Center</div>
            <h1 className="font-display text-xl font-bold leading-tight">Keyholder Leaderboard</h1>
          </div>
          <Link
            to="/rooms"
            className="ml-auto text-[11px] uppercase tracking-[0.16em] text-white/55 hover:text-white/85 px-3 py-1.5 rounded border border-white/10"
            data-testid="leaderboard-browse-rooms"
          >
            Browse rooms →
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <p className="text-sm text-white/55 mb-6">Top rooms by activity. Real numbers from the live database — no padding, no estimates.</p>

        {/* Range pills */}
        <div className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-bold border transition-colors ${
                range === r.value
                  ? "bg-amber-400/15 border-amber-400/50 text-amber-200"
                  : "bg-white/[0.02] border-white/10 text-white/55 hover:text-white/85 hover:border-white/20"
              }`}
              data-testid={`leaderboard-range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Sort pills */}
        <div className="flex flex-wrap items-center gap-2 mb-6" role="group" aria-label="Sort by">
          <span className="text-[10px] uppercase tracking-[0.16em] text-white/35 mr-1">Sort by</span>
          {SORTS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.value}
                onClick={() => setSort(s.value)}
                className={`px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.14em] font-bold border flex items-center gap-1.5 transition-colors ${
                  sort === s.value
                    ? "bg-[#00F0FF]/12 border-[#00F0FF]/45 text-[#00F0FF]"
                    : "bg-white/[0.02] border-white/10 text-white/55 hover:text-white/85 hover:border-white/20"
                }`}
                data-testid={`leaderboard-sort-${s.value}`}
              >
                <Icon className="w-3 h-3" /> {s.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="text-center text-white/40 py-12 text-sm">
            <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
          </div>
        )}
        {!loading && error && (
          <div className="text-center text-red-300 py-6 text-sm" data-testid="leaderboard-error">{error}</div>
        )}

        {/* Empty state — explicit, no placeholders */}
        {!loading && !error && rows.length === 0 && (
          <div
            className="text-center py-16 px-6 border border-dashed border-white/10 rounded-xl"
            data-testid="leaderboard-empty"
          >
            <Trophy className="w-8 h-8 mx-auto text-white/15 mb-3" />
            <div className="text-base font-semibold text-white/70 mb-1">No activity yet — be the first.</div>
            <p className="text-xs text-white/40 mb-5">
              No rooms have activity in this range. Drop into a room to put it on the board.
            </p>
            <Link
              to="/rooms"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-[11px] uppercase tracking-[0.16em] font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F]"
              data-testid="leaderboard-empty-join-rooms"
            >
              Browse rooms →
            </Link>
          </div>
        )}

        {/* Table */}
        {!loading && !error && rows.length > 0 && (
          <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="leaderboard-table">
            <div className="grid grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_70px_70px_70px_70px_70px] gap-2 px-4 py-2.5 bg-white/[0.04] text-[10px] uppercase tracking-[0.16em] text-white/55 font-bold border-b border-white/8">
              <div>#</div>
              <div>Room</div>
              <div className="hidden sm:block">Keyholder</div>
              <div className="text-right">Peak</div>
              <div className="text-right">Joined</div>
              <div className="text-right">Chat</div>
              <div className="text-right">Sessions</div>
              <div className="text-right">Bot</div>
            </div>
            <ul>
              {rows.map((r, idx) => {
                const sortValue = r[sort] ?? 0;
                void sortValue;
                return (
                  <li
                    key={r.slug || idx}
                    className={`grid grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_70px_70px_70px_70px_70px] gap-2 px-4 py-3 items-center text-sm ${
                      idx === 0
                        ? "bg-amber-400/[0.04] border-l-2 border-l-amber-400"
                        : idx % 2 === 0
                          ? "bg-white/[0.012]"
                          : "bg-white/[0.025]"
                    } border-b border-white/[0.05] last:border-b-0`}
                    data-testid={`leaderboard-row-${r.slug}`}
                  >
                    <div className={`font-mono text-base font-bold ${idx === 0 ? "text-amber-300" : idx === 1 ? "text-[#E5E7EB]" : idx === 2 ? "text-orange-300/85" : "text-white/35"}`}>
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {r.room_name}
                        {idx === 0 && <Trophy className="w-3.5 h-3.5 text-amber-300 shrink-0" />}
                      </div>
                      {r.community_handle_url && (
                        <a
                          href={r.community_handle_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-white/35 hover:text-[#00F0FF] truncate flex items-center gap-1 font-mono"
                        >
                          {r.community_handle_url.replace(/^https?:\/\//, "")} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>
                    <div className="hidden sm:block text-xs text-white/55 truncate">{r.keyholder_name || "—"}</div>
                    <div className={`text-right font-mono ${sort === "peak_concurrent" ? "text-[#00F0FF] font-bold" : "text-white/65"}`}>{r.peak_concurrent}</div>
                    <div className={`text-right font-mono ${sort === "participants" ? "text-[#00F0FF] font-bold" : "text-white/65"}`}>{r.participants}</div>
                    <div className={`text-right font-mono ${sort === "chat_messages" ? "text-[#00F0FF] font-bold" : "text-white/65"}`}>{r.chat_messages}</div>
                    <div className={`text-right font-mono ${sort === "sessions_hosted" ? "text-[#00F0FF] font-bold" : "text-white/65"}`}>{r.sessions_hosted}</div>
                    <div className="text-right font-mono text-amber-300/65 flex items-center justify-end gap-1">
                      <Bot className="w-3 h-3" /> {r.bot_activations}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <footer className="mt-10 pt-6 border-t border-white/5 text-[10px] uppercase tracking-[0.22em] text-white/30 flex items-center justify-between">
          <span>Real-time · public · no mock data</span>
          <span>Sorted by: <span className="text-white/55">{sortMeta.label}</span></span>
        </footer>
      </main>
    </div>
  );
}
